import { sessionQuery, useApi, useAuthStore, WakingBanner } from '@classgo/app-kit';
import type { RoleName, Session, TenantSession } from '@classgo/shared';
import {
  AppShell,
  BrandLockup,
  Button,
  Card,
  Header,
  Menu,
  SedeSelector,
  Sidebar,
  useToast,
} from '@classgo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import { config } from '../config';
import type { messages } from '../i18n';
import { useT } from '../i18n';
import { TenantProvider, useTenant } from '../tenant/tenant-provider';

const NAV = [
  { title: null, items: [{ href: '/', key: 'nav.home' }] },
  {
    title: 'nav.setup',
    items: [
      { href: '/configuracion', key: 'nav.checklist', permission: 'tenant:read' },
      { href: '/importar', key: 'nav.imports', permission: 'imports:run' },
    ],
  },
  {
    title: 'nav.structure',
    items: [
      { href: '/sedes', key: 'nav.sedes', permission: 'sedes:read' },
      { href: '/anos', key: 'nav.years', permission: 'academic:read' },
      { href: '/niveles', key: 'nav.levels', permission: 'academic:read' },
      { href: '/grupos', key: 'nav.groups', permission: 'academic:read' },
      { href: '/asignaturas', key: 'nav.subjects', permission: 'subjects:read' },
      { href: '/aulas', key: 'nav.classrooms', permission: 'classrooms:read' },
    ],
  },
  {
    title: 'nav.community',
    items: [
      { href: '/estudiantes', key: 'nav.students', permission: 'students:read' },
      { href: '/acudientes', key: 'nav.guardians', permission: 'guardians:read' },
      { href: '/docentes', key: 'nav.teachers', permission: 'teachers:read' },
    ],
  },
  {
    title: 'nav.academics',
    items: [
      { href: '/plan-de-estudios', key: 'nav.curriculum', permission: 'curriculum:read' },
      { href: '/horario', key: 'nav.timetable', permission: 'timetable:read' },
      { href: '/asistencia', key: 'nav.attendance', permission: 'attendance:read' },
      { href: '/calificaciones', key: 'nav.grades', permission: 'grades:read' },
      { href: '/boletines', key: 'nav.reportCards', permission: 'grades:read' },
      { href: '/comunicados', key: 'nav.announcements', permission: 'communication:read' },
    ],
  },
  {
    title: 'nav.billing',
    items: [{ href: '/cobros', key: 'nav.billing', permission: 'billing:read' }],
  },
  {
    title: 'nav.admin',
    items: [
      { href: '/usuarios', key: 'nav.users', permission: 'users:read' },
      { href: '/roles', key: 'nav.roles', permission: 'roles:read' },
      { href: '/consentimientos', key: 'nav.consents', permission: 'consents:read' },
    ],
  },
  {
    title: 'nav.mentoria',
    items: [{ href: '/mentoria', key: 'nav.mentoriaDemo', permission: 'mentoria:use' }],
  },
  {
    title: 'nav.assistant',
    items: [{ href: '/albert', key: 'nav.albert', permission: 'albert:use', roles: ['TEACHER'] }],
  },
  { title: null, items: [{ href: '/cuenta', key: 'nav.account' }] },
] as const;

type NavHref = (typeof NAV)[number]['items'][number]['href'];

/**
 * Breadcrumb keys per path prefix; the first match wins, so deeper paths come first.
 */
const CRUMBS: readonly { prefix: string; keys: readonly (keyof typeof messages)[] }[] = [
  { prefix: '/configuracion', keys: ['nav.setup', 'nav.checklist'] },
  { prefix: '/usuarios', keys: ['nav.admin', 'nav.users'] },
  { prefix: '/roles', keys: ['nav.admin', 'nav.roles'] },
  { prefix: '/consentimientos', keys: ['nav.admin', 'nav.consents'] },
  { prefix: '/importar', keys: ['nav.setup', 'nav.imports'] },
  { prefix: '/anos', keys: ['nav.structure', 'nav.years'] },
  { prefix: '/sedes', keys: ['nav.structure', 'nav.sedes'] },
  { prefix: '/niveles', keys: ['nav.structure', 'nav.levels'] },
  { prefix: '/grupos', keys: ['nav.structure', 'nav.groups'] },
  { prefix: '/asignaturas', keys: ['nav.structure', 'nav.subjects'] },
  { prefix: '/aulas', keys: ['nav.structure', 'nav.classrooms'] },
  { prefix: '/estudiantes/', keys: ['nav.community', 'nav.students'] },
  { prefix: '/estudiantes', keys: ['nav.community', 'nav.students'] },
  { prefix: '/docentes/', keys: ['nav.community', 'nav.teachers'] },
  { prefix: '/docentes', keys: ['nav.community', 'nav.teachers'] },
  { prefix: '/acudientes', keys: ['nav.community', 'nav.guardians'] },
  { prefix: '/plan-de-estudios', keys: ['nav.academics', 'nav.curriculum'] },
  { prefix: '/horario', keys: ['nav.academics', 'nav.timetable'] },
  { prefix: '/asistencia', keys: ['nav.academics', 'nav.attendance'] },
  { prefix: '/calificaciones', keys: ['nav.academics', 'nav.grades'] },
  { prefix: '/boletines', keys: ['nav.academics', 'nav.reportCards'] },
  { prefix: '/comunicados', keys: ['nav.academics', 'nav.announcements'] },
  { prefix: '/cobros/familias/', keys: ['nav.billing', 'billing.statement.title'] },
  { prefix: '/cobros', keys: ['nav.billing'] },
  { prefix: '/mentoria', keys: ['mentoria.title'] },
  { prefix: '/albert', keys: ['albert.title'] },
  { prefix: '/cuenta', keys: ['account.title'] },
  { prefix: '/', keys: ['home.title'] },
];

/**
 * Guardians and students see their own things, not the school's administration (#63).
 */
/**
 * Whether the session holds a permission (owners and ClassGO staff hold them all).
 * @param {TenantSession} session -- The session from `GET /me`.
 * @param {string} permission -- The `resource:action` key.
 * @returns {boolean} `true` when allowed.
 */
export const can = (session: TenantSession, permission: string): boolean =>
  session.permissions.all || session.permissions.keys.includes(permission);

/**
 * Whether the session may open a screen: it holds the permission, or one of the roles the screen
 * serves without a key (the API's `roleScoped`, ADR-0012: a teacher opens Albert with no key).
 * @param {TenantSession} session -- The session from `GET /me`.
 * @param {string} permission -- The `resource:action` key.
 * @param {readonly RoleName[]} roles -- Roles admitted without the key.
 * @returns {boolean} `true` when allowed.
 */
export const allowed = (
  session: TenantSession,
  permission: string,
  roles: readonly RoleName[] = [],
): boolean => can(session, permission) || roles.some((role) => session.roles.includes(role));

const isFamily = (session: TenantSession) =>
  session.roles.length > 0 &&
  session.roles.every((role) => role === 'GUARDIAN' || role === 'STUDENT');

const FAMILY_NAV = [
  { title: null, items: [{ href: '/', key: 'nav.home' }] },
  { title: null, items: [{ href: '/cuenta', key: 'nav.account' }] },
] as const;

const displayName = (session: Session) =>
  session.kind === 'tenant'
    ? (session.user.givenName ?? session.user.email)
    : (session.staff.givenName ?? session.staff.email);

/**
 * The shell of a school user: sidebar with the lockup, navigation and the always-visible sede
 * selector; header with the screen title and the user menu.
 */
const SchoolShell = ({ onLogout }: { onLogout: () => void }) => {
  const t = useT();
  const navigate = useNavigate();
  const { session, sede, selectSede } = useTenant();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [navigationOpen, setNavigationOpen] = useState(false);
  const crumbs = CRUMBS.find((entry) => pathname.startsWith(entry.prefix))?.keys ?? [];
  return (
    <AppShell
      navigationOpen={navigationOpen}
      onCloseNavigation={() => setNavigationOpen(false)}
      sidebar={
        <Sidebar
          brand={<BrandLockup height={26} />}
          sections={(isFamily(session) ? FAMILY_NAV : NAV)
            .filter((section) => section.title !== 'nav.mentoria' || config.mentoria.enabled)
            // What the person may not do is not shown: each entry names the permission it needs,
            // and a section with nothing left disappears (the API still refuses; this is the UX).
            .map((section) => ({
              ...section,
              items: section.items.filter(
                (item) =>
                  !('permission' in item) ||
                  allowed(session, item.permission, 'roles' in item ? item.roles : []),
              ),
            }))
            .filter((section) => section.items.length > 0)
            .map((section) => ({
              ...(section.title ? { title: t(section.title) } : {}),
              items: section.items.map((item) => ({ href: item.href, label: t(item.key) })),
            }))}
          currentPath={pathname}
          renderLink={(item, className) => (
            <Link
              to={item.href as NavHref}
              className={className}
              onClick={() => setNavigationOpen(false)}
            >
              {item.label}
            </Link>
          )}
          footer={
            <SedeSelector
              sedes={session.sedes}
              selectedId={sede.id}
              onSelect={selectSede}
              {...(can(session, 'sedes:write')
                ? {
                    onAdd: () => {
                      setNavigationOpen(false);
                      void navigate({ to: '/sedes', search: { nueva: true } });
                    },
                  }
                : {})}
            />
          }
        />
      }
      header={
        <>
          <WakingBanner />
          <Header
            title={
              crumbs.length > 0 ? (
                <nav aria-label="Ruta">
                  {crumbs.map((key, index) => (
                    <span key={key}>
                      {index > 0 ? <span className="text-gray-400">{' › '}</span> : null}
                      <span
                        className={index === crumbs.length - 1 ? 'text-gray-900' : 'text-gray-500'}
                      >
                        {t(key)}
                      </span>
                    </span>
                  ))}
                </nav>
              ) : undefined
            }
            onOpenNavigation={() => setNavigationOpen(true)}
            actions={
              <Menu
                label={t('shell.userMenu')}
                trigger={
                  <span className="text-sm font-medium text-gray-700">{displayName(session)}</span>
                }
                items={[
                  {
                    id: 'account',
                    label: t('nav.account'),
                    onSelect: () => void navigate({ to: '/cuenta' }),
                  },
                  { id: 'logout', label: t('shell.logout'), onSelect: onLogout, danger: true },
                ]}
              />
            }
          />
        </>
      }
    >
      {session.actingAs ? (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-card bg-warning-100 px-4 py-2 text-sm text-warning-700"
        >
          <span>
            {t('actAs.banner', {
              tenant: session.tenant.name,
              until: new Date(session.actingAs.expiresAt).toLocaleTimeString('es-CO', {
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}
          </span>
          <Button size="sm" variant="secondary" onClick={onLogout}>
            {t('actAs.leave')}
          </Button>
        </div>
      ) : null}
      <Outlet />
    </AppShell>
  );
};

/**
 * ClassGO staff signed into the school app by mistake: point them to the staff console.
 */
const StaffNotice = ({ onLogout }: { onLogout: () => void }) => {
  const t = useT();
  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <h1 className="text-lg font-semibold text-gray-900">{t('staffNotice.title')}</h1>
        <p className="mt-2 text-sm text-gray-600">{t('staffNotice.body')}</p>
        <Button className="mt-5" variant="secondary" onClick={onLogout}>
          {t('shell.logout')}
        </Button>
      </Card>
    </main>
  );
};

/**
 * Everything signed-in: loads the session and routes staff and school users to their shells. An
 * ended session is handled by `SessionWatcher` at the root, whatever screen is showing.
 */
export const AppLayout = () => {
  const api = useApi();
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const auth = useAuthStore();
  const { data: session } = useSuspenseQuery(sessionQuery(api));

  const logout = async () => {
    // Staff acting inside a school only leave the school: their console session stays alive.
    if (!auth.getSnapshot().actAs) {
      try {
        await api.request('/auth/logout', { method: 'POST' });
      } catch {
        // Revocation failed or the token was already gone: the local session ends regardless.
      }
      toast.show({ tone: 'info', title: t('shell.loggedOut') });
    }
    auth.signOutLocal('user');
    void navigate({ to: '/login', search: { redirect: undefined } });
  };

  if (session.kind === 'staff') {
    return <StaffNotice onLogout={() => void logout()} />;
  }
  return (
    <TenantProvider session={session as TenantSession}>
      <SchoolShell onLogout={() => void logout()} />
    </TenantProvider>
  );
};
